import {
	TableContainer,
	Table,
	TableHead,
	TableBody,
	TableRow,
	TableCell,
	Skeleton,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogContentText,
	DialogActions,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import React from 'react';
import { useMutation, useQueryClient } from 'react-query';
import {
	CerebroDrawer,
	CerebroHeading,
	CerebroSecondaryButton,
	CerebroText,
	CerebroTextButton,
} from '../../shared/components';
import { useApi } from '../../shared/context';
import { QueryKeys, useDataRoomDiligenceRequestsQuery } from '../../shared/hooks';
import { DataRoomDiligenceRequest, DataRoomDocument, DiligenceRequestLink } from '../../shared/models';
import { CerebroApiEndpoints } from '../../shared/utils/api';
import { getApiResponseData } from '../../shared/utils/axios';

interface LinkDocumentDrawerProps {
	dataRoomGuid: string | undefined;
	document: DataRoomDocument | null;
	close: () => void;
}

export const LinkDocumentDrawer: React.FC<LinkDocumentDrawerProps> = ({ dataRoomGuid, document, close }) => {
	const api = useApi();
	const snackbar = useSnackbar();
	const client = useQueryClient();
	const requests = useDataRoomDiligenceRequestsQuery(dataRoomGuid);
	const [confirmLink, setConfirmLink] = React.useState<DataRoomDiligenceRequest | null>(null);

	const isAlreadyLinked = (request: DataRoomDiligenceRequest): boolean => {
		if (request.links && request.links.length > 0) {
			const link = request.links.filter(w => w.document.guid == document?.guid);
			return link.length > 0;
		}

		return false;
	};

	const onLinkConfirmation = () => {
		if (confirmLink !== null) linkRequestMutation.mutate(confirmLink);
	};

	const linkRequestMutation = useMutation(
		async (request: DataRoomDiligenceRequest) => {
			const postValues = {
				documentGuid: document?.guid,
			};

			const { data, error } = await getApiResponseData<DiligenceRequestLink>(
				api.post(CerebroApiEndpoints.DataRoomDiligenceRequests.Links(dataRoomGuid, request.guid), postValues)
			);

			if (error) {
				throw new Error(error.response?.data);
			}

			return data;
		},
		{
			onSuccess: () => {
				client.invalidateQueries(QueryKeys.DataRoomDiligenceRequests.Root(dataRoomGuid));
				snackbar.enqueueSnackbar('Document successfully linked to diligence request.', { variant: 'success' });
				setConfirmLink(null);
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, {
					variant: 'error',
				});
			},
		}
	);

	return (
		<CerebroDrawer open={document != null} onClose={close}>
			<CerebroHeading component="h1" size="h1">
				Link Document to Diligence Request
			</CerebroHeading>
			<CerebroHeading sx={{ marginBottom: 3 }} component="h3" size="h3">
				{document?.name}
			</CerebroHeading>

			<TableContainer>
				<Table>
					<TableHead>
						<TableRow>
							<TableCell>Title</TableCell>
							<TableCell>Category</TableCell>
							<TableCell>Link</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{requests.isLoading && (
							<>
								<TableRow>
									<TableCell colSpan={2}>
										<Skeleton height={60} />
									</TableCell>
								</TableRow>
								<TableRow>
									<TableCell colSpan={2}>
										<Skeleton height={60} />
									</TableCell>
								</TableRow>
								<TableRow>
									<TableCell colSpan={2}>
										<Skeleton height={60} />
									</TableCell>
								</TableRow>
							</>
						)}
						{requests.isSuccess && (
							<>
								{requests.data.map(request => (
									<TableRow key={request.guid}>
										<TableCell>{request.title}</TableCell>
										<TableCell>
											{isAlreadyLinked(request) ? (
												<CerebroText sx={{ textAlign: 'center' }}>Already Linked</CerebroText>
											) : (
												<CerebroSecondaryButton onClick={() => setConfirmLink(request)}>
													Link
												</CerebroSecondaryButton>
											)}
										</TableCell>
									</TableRow>
								))}
								{requests.data.length == 0 && (
									<TableRow>
										<TableCell colSpan={2}>
											<CerebroText>
												No diligence requests exist for this data room to link the selected
												document to.
											</CerebroText>
										</TableCell>
									</TableRow>
								)}
							</>
						)}
					</TableBody>
				</Table>
			</TableContainer>

			<Dialog open={confirmLink !== null} onClose={close}>
				<DialogTitle>Confirm Link</DialogTitle>
				<DialogContent>
					<DialogContentText>
						Are you sure you want to link {document?.name} to {confirmLink?.title} diligence request?
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<CerebroTextButton onClick={() => setConfirmLink(null)}>No</CerebroTextButton>
					<CerebroTextButton onClick={onLinkConfirmation}>Yes</CerebroTextButton>
				</DialogActions>
			</Dialog>
		</CerebroDrawer>
	);
};
